import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from "react";
import { useEngine, useFrameStats } from "./EngineProvider.js";
import { cssRotationForOrientation, normalizeOrientation } from "./orientation.js";
/**
 * Presentational component: mounts a <canvas> the EngineProvider attaches to,
 * optionally reconciles a `camera` prop to engine.startCamera/stopCamera, and
 * shows an optional debug overlay.
 *
 * The engine itself lives in <EngineProvider>; this component is a leaf and
 * pulls everything from context. To use it standalone:
 *
 * @example
 * <EngineProvider options={engineOptions}>
 *   <LensCanvas camera={{ deviceId }} fit="contain" showDebugOverlay />
 * </EngineProvider>
 *
 * @param {Object} props
 * @param {import("./EngineProvider.jsx").CameraConfig | null} [props.camera]
 *   Declarative camera config. When changed, the component calls actions.startCamera()
 *   to reconcile. Pass `null` to stop the camera. Omit to leave camera control to the caller.
 * @param {import("./EngineProvider.jsx").StaticImageConfig | string} [props.image]
 *   Declarative static-image input. Takes precedence over `camera` and is drawn
 *   into a retained input canvas that LensCore processes like any other media source.
 * @param {"contain" | "cover" | "fill"} [props.fit] - object-fit for the canvas.
 * @param {boolean} [props.showDebugOverlay] - Renders a small fps + source overlay.
 * @param {import("./orientation.js").Orientation | string | number} [props.outputOrientation]
 *   Visual rotation applied to the canvas via CSS `transform`. Does not modify
 *   the underlying pixel buffer — downstream consumers reading the canvas
 *   pixels (e.g. Syphon, screen-grabbed Browser Sources) see the unrotated
 *   image. Accepts `"CW0"`, `"CW90"`, `"CW180"`, `"CW270"`, or equivalent
 *   degree/index numbers.
 * @param {string} [props.className] - Applied to the wrapper div.
 * @param {React.CSSProperties} [props.style] - Applied to the wrapper div.
 */
export default function LensCanvas({ camera, image, fit = "contain", showDebugOverlay = false, outputOrientation, className = "", style, }) {
    const { canvasRef, ready, actions } = useEngine();
    useEffect(() => {
        if (!ready)
            return undefined;
        let cancelled = false;
        if (image !== undefined) {
            actions.attachStaticImage(image).catch(() => undefined);
        }
        else if (camera !== undefined) {
            actions.startCamera(camera).catch(() => undefined);
        }
        return () => {
            cancelled = true;
            // Only stop when the consumer explicitly passes null/undefined later;
            // otherwise the next effect run will re-attach with the new config.
            void cancelled;
        };
    }, [
        ready,
        typeof image === "object" ? image.url : image,
        typeof image === "object" ? image.fps : undefined,
        typeof image === "object" ? image.orientation : undefined,
        camera?.deviceId,
        camera?.preferredFormat?.width,
        camera?.preferredFormat?.height,
        camera?.preferredFormat?.fps,
        camera?.orientation,
        actions,
    ]);
    const canvasTransform = outputOrientation !== undefined
        ? cssRotationForOrientation(normalizeOrientation(outputOrientation))
        : "";
    return (_jsxs("div", { className: `lens-canvas-wrapper ${className}`, style: style, children: [_jsx("canvas", { ref: canvasRef, style: {
                    objectFit: fit,
                    width: "100%",
                    height: "100%",
                    display: "block",
                    transform: canvasTransform || undefined,
                    transformOrigin: canvasTransform ? "center center" : undefined,
                } }), showDebugOverlay && _jsx(DebugOverlay, {})] }));
}
function DebugOverlay() {
    const { source, error } = useEngine();
    const stats = useFrameStats();
    const fpsText = stats.measuredFps > 0 ? stats.measuredFps.toFixed(1) : "--";
    const sourceText = source ? `${source.label || "camera"} ${source.width}×${source.height}@${source.fps}` : "no source";
    return (_jsxs("div", { className: "lens-debug-overlay", style: {
            position: "absolute",
            top: 8,
            left: 8,
            padding: "4px 8px",
            borderRadius: 4,
            background: "rgba(0,0,0,0.55)",
            color: "#dde3ec",
            font: "11px/1.3 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            pointerEvents: "none",
            whiteSpace: "pre",
        }, children: [`${fpsText} fps · frames ${stats.frameCount} · ${sourceText}`, error ? `\nerror: ${error.message}` : ""] }));
}
//# sourceMappingURL=LensCanvas.js.map